/**
 * Utility to get expected output structure for different node types
 */

export interface FieldPath {
  path: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
}

/**
 * Get expected output fields for a given task type
 */
export function getNodeOutputSchema(taskType: string): FieldPath[] {
  const schemas: Record<string, FieldPath[]> = {
    // Data source nodes
    data_source_item: [
      { path: 'item', label: 'Item Object', type: 'object', description: 'The fetched item' },
      { path: 'item.id', label: 'Item ID', type: 'string' },
      { path: 'item.external_id', label: 'Item External ID', type: 'number' },
      { path: 'item.summary', label: 'Item Summary', type: 'string' },
      { path: 'item.description', label: 'Item Description', type: 'string' },
      { path: 'item.type', label: 'Item Type', type: 'string' },
      { path: 'item.status', label: 'Item Status', type: 'string' },
      { path: 'external_id', label: 'External ID', type: 'number' },
      { path: 'properties', label: 'Selected Properties', type: 'array' },
    ],
    data_source_tracker: [
      { path: 'items', label: 'Items Array', type: 'array', description: 'Array of fetched items' },
      { path: 'count', label: 'Item Count', type: 'number' },
      { path: 'tracker_id', label: 'Tracker ID', type: 'string' },
      { path: 'properties', label: 'Selected Properties', type: 'array' },
      { path: 'fields', label: 'Tracker Schema Fields', type: 'string', description: 'One line per visible field, with its choice options' },
      { path: 'json', label: 'Tracker Schema (JSON)', type: 'string', description: 'Raw tracker field definitions' },
    ],
    data_source_project: [
      { path: 'items', label: 'Items Array', type: 'array', description: 'Array of fetched items from project' },
      { path: 'count', label: 'Item Count', type: 'number' },
      { path: 'project_id', label: 'Project ID', type: 'string' },
      { path: 'properties', label: 'Selected Properties', type: 'array' },
    ],
    data_source_chapter: [
      { path: 'items', label: 'Items Array', type: 'array', description: 'Array of fetched items covered by the chapter' },
      { path: 'count', label: 'Item Count', type: 'number' },
      { path: 'chapter_id', label: 'Chapter ID', type: 'string' },
      { path: 'properties', label: 'Selected Properties', type: 'array' },
    ],
    data_source_outline: [
      { path: 'outline', label: 'Outline Tree', type: 'string', description: 'Indented "- summary [id]" line per item, children under their parent' },
      { path: 'json', label: 'Outline (JSON)', type: 'string', description: 'Raw {nodes: [{id, parent_id, depth, summary}], truncated}' },
    ],
    data_source_tracker_schema: [
      { path: 'fields', label: 'Tracker Fields', type: 'string', description: 'One line per visible field, with its choice options' },
      { path: 'json', label: 'Tracker Schema (JSON)', type: 'string', description: 'Raw tracker field definitions' },
    ],
    data_source_tracker_list: [
      { path: 'trackers', label: 'Tracker list', type: 'string', description: 'One "- [id] name" line per tracker' },
      { path: 'json', label: 'Tracker List (JSON)', type: 'string', description: "Raw list of the project's trackers (id and name)" },
    ],

    // Analysis nodes
    classification: [
      { path: 'output.primary_type', label: 'Primary Type', type: 'string', description: 'Functional, Non-Functional, etc.' },
      { path: 'output.abstraction_level', label: 'Abstraction Level', type: 'string', description: 'System, Component, etc.' },
      { path: 'output.confidence', label: 'Confidence Score', type: 'number' },
      { path: 'output.reasoning', label: 'Reasoning', type: 'string' },
    ],
    ai_review: [
      { path: 'output.violations', label: 'Violations Array', type: 'array', description: 'List of detected violations' },
      { path: 'output.score', label: 'Quality Score', type: 'number' },
      { path: 'output.passed', label: 'Passed Review', type: 'boolean' },
    ],
    weak_words: [
      { path: 'output.weak_words_found', label: 'Weak Words Array', type: 'array' },
      { path: 'output.count', label: 'Weak Word Count', type: 'number' },
    ],

    // Authoring nodes
    authoring: [
      { path: 'output.improved_text', label: 'Improved Text', type: 'string' },
      { path: 'output.changes', label: 'Changes Array', type: 'array' },
      { path: 'output.quality_score', label: 'Quality Score', type: 'number' },
    ],

    // Testing nodes
    test_condition: [
      { path: 'output.test_conditions', label: 'Test Conditions Array', type: 'array' },
      { path: 'output.coverage', label: 'Coverage Score', type: 'number' },
    ],
    test_case: [
      { path: 'output.test_cases', label: 'Test Cases Array', type: 'array' },
      { path: 'output.count', label: 'Test Case Count', type: 'number' },
    ],

    // Semantic nodes
    semantic_grouping: [
      { path: 'output.groups', label: 'Semantic Groups Array', type: 'array' },
      { path: 'output.ungrouped', label: 'Ungrouped Items', type: 'array' },
    ],

    // Decomposition nodes
    requirement_decomposition: [
      { path: 'output.subrequirements', label: 'Sub-requirements Array', type: 'array' },
      { path: 'output.count', label: 'Sub-requirement Count', type: 'number' },
    ],

    // Analysis nodes
    cross_item_analysis: [
      { path: 'output.conflicts', label: 'Conflicts Array', type: 'array' },
      { path: 'output.redundancies', label: 'Redundancies Array', type: 'array' },
    ],
    impact_analysis: [
      { path: 'output.affected_items', label: 'Affected Items Array', type: 'array' },
      { path: 'output.severity', label: 'Impact Severity', type: 'string' },
    ],
    traceability_analysis: [
      { path: 'output.links', label: 'Traceability Links Array', type: 'array' },
      { path: 'output.coverage', label: 'Traceability Coverage', type: 'number' },
    ],
  };

  // TODO: only ~14 of ~35 TaskType values have real schemas above; the rest
  // fall back to this generic pair. Expanding per-task-type coverage is a
  // separate, larger effort requiring per-task-type output-shape research.
  return schemas[taskType] || [
    { path: 'output', label: 'Output Object', type: 'object' },
    { path: 'status', label: 'Status', type: 'string' },
  ];
}

/**
 * Filter field paths to only show arrays (for loop configuration)
 */
export function getArrayFields(fields: FieldPath[]): FieldPath[] {
  return fields.filter(f => f.type === 'array');
}

/**
 * Filter field paths to exclude arrays (for conditional configuration)
 */
export function getScalarFields(fields: FieldPath[]): FieldPath[] {
  return fields.filter(f => f.type !== 'array' && f.type !== 'object');
}
